import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, Mic, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { aplicarErrosDoServidor } from '@/api/erros';
import type {
  AtualizarRascunhoDto,
  AtualizarRascunhoDtoDeclaracaoTratamento,
  AtualizarRascunhoDtoEvolucao,
  AtualizarRascunhoDtoTipo,
  GravacaoConsultaResponse,
  ProntuarioResponse,
} from '@/api/generated/model';
import {
  gravacoesConsultaControllerDescartar,
  useGravacoesConsultaControllerObter,
} from '@/api/generated/gravações-de-consulta/gravações-de-consulta';
import {
  getHistoricoControllerLinhaDoTempoQueryKey,
  getHistoricoControllerResumoQueryKey,
} from '@/api/generated/historico/historico';
import { usePacientesControllerObter } from '@/api/generated/pacientes/pacientes';
import {
  getProntuariosControllerListarQueryKey,
  prontuariosControllerAdicionarArquivo,
  prontuariosControllerAdicionarExame,
  prontuariosControllerAtualizarRascunho,
  prontuariosControllerDescartarRascunho,
  prontuariosControllerRegistrar,
  prontuariosControllerRemoverArquivo,
  prontuariosControllerRemoverExame,
  prontuariosControllerRenomearArquivo,
} from '@/api/generated/prontuarios/prontuarios';
import {
  getTratamentoVigenteControllerTratamentoVigenteQueryKey,
  getTratamentosControllerListarQueryKey,
  useTratamentoVigenteControllerTratamentoVigente,
  useTratamentosControllerAtualizar,
  useTratamentosControllerCriar,
  useTratamentosControllerSincronizar,
} from '@/api/generated/tratamentos/tratamentos';
import { EditorDeTratamento } from '@/features/historico/components/EditorDeTratamento';
import {
  type ItensSelecionados,
  itensValidos,
  type OperacoesNoServidor,
} from '@/features/historico/itensDoProntuario';
import { ArquivosEExames } from '@/features/historico/components/ArquivosEExames';
import { GravacaoDeConsulta } from '@/features/historico/components/GravacaoDeConsulta';
import {
  descreverSincronizacao,
  itensParaSincronizar,
  linhasDoTratamento,
  prontuarioSchema,
  type FormularioProntuario,
} from '@/features/historico/prontuarioForm';
import { isoParaLocal, localParaISO, ROTULO_TIPO, TIPOS } from '@/features/historico/rotulos';
import { diaDoAtendimento, EVOLUCOES, ROTULO_EVOLUCAO } from '@/features/historico/tratamento';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { ApiError } from '@/api/http-client';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { toast } from '@/shared/ui/sonner';
import { Textarea } from '@/shared/ui/textarea';

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  pacienteId: string;
  /**
   * O RASCUNHO sendo editado — criado no clique em "Novo prontuário" ou
   * reaberto em "Continuar". Existe no servidor desde já: é ele que recebe as
   * fotos/PDFs, os exames e a gravação enquanto a consulta acontece.
   */
  rascunho: ProntuarioResponse | null;
}

/** Espera depois da última tecla para salvar o rascunho sozinho. */
const ESPERA_DO_SALVAMENTO_MS = 1_500;

type EstadoDoSalvamento = 'salvo' | 'salvando' | 'falhou';

/** O formulário como o rascunho foi deixado. `atendidoEm` já vem do servidor (a hora em que o rascunho nasceu). */
function valoresDoRascunho(rascunho: ProntuarioResponse | null): FormularioProntuario {
  return {
    tipo: rascunho?.tipo ?? 'CONSULTA',
    titulo: rascunho?.titulo ?? '',
    conteudo: rascunho?.conteudo ?? '',
    atendidoEm: isoParaLocal(rascunho?.atendidoEm ?? new Date().toISOString()),
    declaracaoTratamento: rascunho?.declaracaoTratamento ?? '',
    tituloTratamento: '',
    evolucao: rascunho?.evolucao ?? '',
    evolucaoObservacoes: rascunho?.evolucaoObservacoes ?? '',
    medicamentos: [],
  };
}

function itensDoRascunho(rascunho: ProntuarioResponse | null): ItensSelecionados {
  return {
    arquivos: (rascunho?.arquivos ?? []).map((a) => ({
      chave: a.arquivoId,
      titulo: a.titulo,
      nomeOriginal: a.nomeOriginal,
      mimeType: a.mimeType,
      tamanhoBytes: a.tamanhoBytes,
    })),
    exames: (rascunho?.exames ?? []).map((e) => ({
      exameId: e.exameId,
      nome: e.nome,
      coletadoEm: e.coletadoEm,
      origem: e.origem,
    })),
  };
}

/**
 * Os campos do prontuário como o servidor os recebe — no salvamento automático
 * e no registrar. Data inválida (campo apagado no meio da digitação) fica de
 * fora em vez de derrubar o salvamento.
 */
function camposParaEnvio(dados: FormularioProntuario): AtualizarRascunhoDto {
  const atendidoEm = dados.atendidoEm ? localParaISO(dados.atendidoEm) : '';
  return {
    tipo: dados.tipo as AtualizarRascunhoDtoTipo,
    titulo: dados.titulo.trim() ? dados.titulo.trim().slice(0, 200) : null,
    conteudo: dados.conteudo.slice(0, 20_000),
    ...(atendidoEm && !Number.isNaN(Date.parse(atendidoEm)) ? { atendidoEm } : {}),
    declaracaoTratamento: (dados.declaracaoTratamento || null) as AtualizarRascunhoDtoDeclaracaoTratamento,
    evolucao: (dados.evolucao || null) as AtualizarRascunhoDtoEvolucao,
    evolucaoObservacoes: dados.evolucaoObservacoes.trim() ? dados.evolucaoObservacoes.slice(0, 2_000) : null,
  };
}

/**
 * O prontuário — e, dentro dele, o tratamento (Fase 1d).
 *
 * 🔴 **É por aqui que o dado de tratamento entra.** Não há tela separada: o
 * veterinário abre o prontuário no atendimento, e o que ele já ia escrever em
 * texto livre passa a ter campo. Uma tela de tratamento à parte seria aberta
 * por ninguém, e a Análise Comparativa filtraria uma coorte vazia.
 *
 * ⚠️ `declaracaoTratamento` é ANULÁVEL. "Não declarado" (o botão nenhum
 * marcado) é o estado de todo prontuário antigo e de todo prontuário criado
 * pelo app — e não significa `SEM_TRATAMENTO`, que é uma afirmação clínica.
 */
export function ProntuarioFormDialogo({ aberto, aoFechar, pacienteId, rascunho }: Props) {
  const queryClient = useQueryClient();
  const rascunhoId = rascunho?.id ?? '';

  const sincronizar = useTratamentosControllerSincronizar();
  const criarTratamento = useTratamentosControllerCriar();
  const renomearTratamento = useTratamentosControllerAtualizar();

  const form = useForm<FormularioProntuario>({
    resolver: zodResolver(prontuarioSchema),
    defaultValues: valoresDoRascunho(rascunho),
  });

  const {
    formState: { errors, isSubmitting },
  } = form;

  const declaracao = form.watch('declaracaoTratamento');
  const atendidoEm = form.watch('atendidoEm');
  const evolucao = form.watch('evolucao');
  const tipo = form.watch('tipo');

  /**
   * ⚠️ `emData` é o dia do ATENDIMENTO, nunca "hoje".
   *
   * Um prontuário retroativo tem de abrir com a dose que valia NAQUELA
   * consulta. Pré-carregá-lo com a dose de hoje faria o diff do servidor
   * acusar um ajuste que nunca aconteceu — e a linha falsa entraria no
   * histórico como se fosse clínica.
   *
   * O corte é em UTC porque é assim que o servidor deriva a data da vigência a
   * partir do mesmo `atendidoEm` que recebe no salvar.
   */
  const atendidoEmAdiado = useDebounce(atendidoEm, 400);
  const emData = atendidoEmAdiado ? diaDoAtendimento(localParaISO(atendidoEmAdiado)) : '';

  const vigente = useTratamentoVigenteControllerTratamentoVigente(
    pacienteId,
    emData ? { emData } : undefined,
    { query: { enabled: aberto && Boolean(pacienteId) && Boolean(emData) } },
  );

  // O peso corrente só alimenta a SUGESTÃO de dose/kg — o valor gravado é o do
  // campo, que continua editável.
  const paciente = usePacientesControllerObter(pacienteId, {
    query: { enabled: aberto && Boolean(pacienteId) },
  });
  const pesoKg = paciente.data?.pesoKg ?? null;

  const jaPreenchido = useRef('');
  /** Trava de duplo submit: o 409 `VIGENCIA_DUPLICADA` é a rede, não a porta. */
  const enviando = useRef(false);

  // ── Consulta gravada (item 15) — só no prontuário NOVO ──────────────────────
  const [painelDeGravacao, setPainelDeGravacao] = useState(false);
  const [gravacaoEmAndamento, setGravacaoEmAndamento] = useState(false);
  /** O texto gerado, esperando o veterinário decidir o que fazer com o Conteúdo que ele já tinha escrito. */
  const [textoGeradoPendente, setTextoGeradoPendente] = useState<string | null>(null);
  const [confirmandoFechar, setConfirmandoFechar] = useState(false);
  const [confirmandoDescarte, setConfirmandoDescarte] = useState(false);
  const [descartando, setDescartando] = useState(false);
  /** Id da gravação em andamento — descartada se o diálogo fechar no meio dela. */
  const gravacaoIdRef = useRef<string | null>(null);

  // A gravação que o rascunho já tem ("Continuar"): o painel só monta com ela
  // carregada, porque abre direto no resultado.
  const gravacaoDoRascunhoId = rascunho?.gravacaoConsulta?.id ?? '';
  const gravacaoExistente = useGravacoesConsultaControllerObter(gravacaoDoRascunhoId, {
    query: { enabled: aberto && Boolean(gravacaoDoRascunhoId) },
  });

  // ── Arquivos e exames (item 16) — gravados no rascunho a cada ação ─────────
  const [itens, setItens] = useState<ItensSelecionados>(() => itensDoRascunho(rascunho));
  const [arquivosEnviando, setArquivosEnviando] = useState(0);
  /** Ações de arquivo/exame ainda no ar — o registrar espera por elas. */
  const pendentes = useRef(new Set<Promise<unknown>>());

  const acompanhar = useCallback(<T,>(promessa: Promise<T>): Promise<T> => {
    pendentes.current.add(promessa);
    void promessa.finally(() => pendentes.current.delete(promessa)).catch(() => {});
    return promessa;
  }, []);

  const servidor = useMemo<OperacoesNoServidor>(
    () => ({
      enviarArquivo: async (arquivo, titulo) => {
        const salvo = await acompanhar(prontuariosControllerAdicionarArquivo(rascunhoId, { arquivo, titulo }));
        return {
          chave: salvo.arquivoId,
          titulo: salvo.titulo,
          nomeOriginal: salvo.nomeOriginal,
          mimeType: salvo.mimeType,
          tamanhoBytes: salvo.tamanhoBytes,
        };
      },
      renomearArquivo: async (chave, titulo) => {
        await acompanhar(prontuariosControllerRenomearArquivo(rascunhoId, chave, { titulo }));
      },
      removerArquivo: (chave) => acompanhar(prontuariosControllerRemoverArquivo(rascunhoId, chave)),
      vincularExame: async (exameId) => {
        await acompanhar(prontuariosControllerAdicionarExame(rascunhoId, { exameId }));
      },
      desvincularExame: (exameId) => acompanhar(prontuariosControllerRemoverExame(rascunhoId, exameId)),
    }),
    [rascunhoId, acompanhar],
  );

  // ── Salvamento automático ──────────────────────────────────────────────────
  const [salvamento, setSalvamento] = useState<EstadoDoSalvamento>('salvo');
  /** O que o servidor já tem — não reenvia o mesmo estado. */
  const ultimoSalvo = useRef('');
  const salvandoAgora = useRef<Promise<void> | null>(null);

  useEffect(() => {
    if (!aberto) return;
    form.reset(valoresDoRascunho(rascunho));
    ultimoSalvo.current = JSON.stringify(camposParaEnvio(valoresDoRascunho(rascunho)));
    setSalvamento('salvo');
    jaPreenchido.current = '';
    enviando.current = false;
    setPainelDeGravacao(Boolean(rascunho?.gravacaoConsulta));
    setGravacaoEmAndamento(false);
    setTextoGeradoPendente(null);
    setConfirmandoFechar(false);
    setConfirmandoDescarte(false);
    gravacaoIdRef.current = null;
    setItens(itensDoRascunho(rascunho));
    setArquivosEnviando(0);
    pendentes.current.clear();
    // `form` fora das deps de propósito: a identidade do objeto do RHF muda a
    // cada render e o reset entraria em laço, apagando o que se digita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, rascunhoId]);

  const valores = form.watch();
  const valoresAdiados = useDebounce(JSON.stringify(camposParaEnvio(valores)), ESPERA_DO_SALVAMENTO_MS);

  const salvarRascunho = useCallback(
    async (campos: string): Promise<void> => {
      if (!rascunhoId || campos === ultimoSalvo.current || enviando.current) return;
      setSalvamento('salvando');
      const envio = prontuariosControllerAtualizarRascunho(rascunhoId, JSON.parse(campos) as AtualizarRascunhoDto)
        .then(() => {
          ultimoSalvo.current = campos;
          setSalvamento('salvo');
        })
        .catch(() => setSalvamento('falhou'));
      salvandoAgora.current = envio;
      await envio;
    },
    [rascunhoId],
  );

  useEffect(() => {
    if (aberto) void salvarRascunho(valoresAdiados);
  }, [aberto, valoresAdiados, salvarRascunho]);

  const tratamentoVigente = vigente.data?.tratamento ?? null;

  /**
   * 🔴 A CONTINUIDADE entre atendimentos.
   *
   * O editor abre já preenchido com o tratamento que valia em `atendidoEm`.
   * Quem não mexer em nada salva sem gerar linha nenhuma (`semAlteracao`), que
   * é o desfecho comum de um retorno — e quem mexer gera exatamente a
   * diferença, calculada pelo servidor.
   *
   * Num prontuário NOVO o botão "Em tratamento" já vem marcado quando o animal
   * está medicado: é fato registrado, não suposição. Editando um prontuário
   * antigo, o que está gravado manda — inclusive o "não declarado".
   */
  useEffect(() => {
    if (!aberto) return;
    const dados = vigente.data;
    if (!dados?.tratamento) return;

    // ⚠️ A chave NÃO inclui a declaração — de propósito. Incluí-la fazia o
    // efeito rodar duas vezes (a segunda porque ele mesmo tinha acabado de
    // mudar a declaração), e o segundo `reset` remontava as linhas: o que já
    // estivesse digitado voltava ao valor pré-carregado.
    const chave = `${dados.emData}|${dados.tratamento.id}`;
    if (jaPreenchido.current === chave) return;
    // Rascunho reaberto em que a pessoa já declarou "sem tratamento": vale o que ela disse.
    if (form.getValues('declaracaoTratamento') === 'SEM_TRATAMENTO') {
      jaPreenchido.current = chave;
      return;
    }

    // Editando um prontuário antigo, o que está GRAVADO manda — inclusive o
    // "não declarado". Só o prontuário novo ganha a declaração automática.

    jaPreenchido.current = chave;
    form.reset({
      ...form.getValues(),
      declaracaoTratamento: 'EM_TRATAMENTO',
      tituloTratamento: dados.tratamento.titulo,
      medicamentos: linhasDoTratamento(dados.tratamento),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, vigente.data, declaracao]);

  /**
   * Os dois botões da §3.4, mutuamente exclusivos. Clicar no que já está
   * marcado volta ao "não declarado" — sem isso não haveria como desfazer uma
   * declaração feita por engano.
   */
  function declarar(valor: 'EM_TRATAMENTO' | 'SEM_TRATAMENTO') {
    const novo = declaracao === valor ? '' : valor;

    if (novo === 'EM_TRATAMENTO' && tratamentoVigente && vigente.data) {
      // Marca a chave para o efeito de pré-carga não repetir o `reset` — dois
      // resets seguidos remontam as linhas e descartam o que já foi digitado.
      jaPreenchido.current = `${vigente.data.emData}|${tratamentoVigente.id}`;
      form.reset({
        ...form.getValues(),
        declaracaoTratamento: novo,
        tituloTratamento: tratamentoVigente.titulo,
        medicamentos: linhasDoTratamento(tratamentoVigente),
      });
      return;
    }

    form.setValue('declaracaoTratamento', novo, { shouldValidate: false });
  }

  /**
   * A gravação terminou: o rascunho da IA (ou a transcrição, se o resumo
   * falhou) vai para o Conteúdo. Se o veterinário já tinha escrito algo, ele
   * escolhe — sobrescrever o que ele digitou sem perguntar seria perder texto.
   */
  function aoConcluirGravacao(gravacao: GravacaoConsultaResponse) {
    const texto = gravacao.resumoFormatado ?? gravacao.transcricao;
    if (!texto) return;

    if (form.getValues('conteudo').trim()) setTextoGeradoPendente(texto);
    else form.setValue('conteudo', texto, { shouldValidate: true });
  }

  function aplicarTextoGerado(modo: 'substituir' | 'acrescentar') {
    if (!textoGeradoPendente) return;
    const atual = form.getValues('conteudo').trim();
    form.setValue('conteudo', modo === 'substituir' ? textoGeradoPendente : `${atual}\n\n${textoGeradoPendente}`, {
      shouldValidate: true,
    });
    setTextoGeradoPendente(null);
  }

  const aoMudarGravacaoEmAndamento = useCallback((valor: boolean) => setGravacaoEmAndamento(valor), []);
  const aoMudarGravacaoId = useCallback((id: string | null) => {
    gravacaoIdRef.current = id;
  }, []);

  /**
   * Fechar NÃO perde nada: o rascunho fica salvo e reaparece em "Em
   * andamento". Só uma gravação em andamento pede confirmação — ela não tem
   * como continuar com o diálogo fechado.
   */
  function fecharComCuidado() {
    if (gravacaoEmAndamento) {
      setConfirmandoFechar(true);
      return;
    }
    void fecharGuardando();
  }

  async function fecharGuardando() {
    await salvarRascunho(JSON.stringify(camposParaEnvio(form.getValues())));
    await queryClient.invalidateQueries({ queryKey: getProntuariosControllerListarQueryKey() });
    toast.info('Rascunho guardado — continue quando quiser.');
    aoFechar();
  }

  function descartarGravacaoEmAndamento() {
    const id = gravacaoIdRef.current;
    gravacaoIdRef.current = null;
    // Sem esperar: o diálogo já está fechando, e uma sobra aqui é só áudio de rascunho.
    if (id) void gravacoesConsultaControllerDescartar(id).catch(() => {});
  }

  async function descartarRascunho() {
    setDescartando(true);
    try {
      await Promise.allSettled([...pendentes.current]);
      await prontuariosControllerDescartarRascunho(rascunhoId);
      await queryClient.invalidateQueries({ queryKey: getProntuariosControllerListarQueryKey() });
      toast.success('Rascunho descartado.');
      setConfirmandoDescarte(false);
      aoFechar();
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível descartar o rascunho.');
    } finally {
      setDescartando(false);
    }
  }

  /** Idem para a evolução, que também é anulável. */
  function marcarEvolucao(valor: string) {
    form.setValue('evolucao', evolucao === valor ? '' : valor);
  }

  /**
   * Manda o ESTADO como ficou na tela; o servidor faz o diff.
   *
   * Devolve a frase do resultado, ou `null` quando nada mudou — e aí a tela
   * fica calada. `semAlteracao: true` é a consulta de acompanhamento sem
   * ajuste, o caso comum, não uma falha.
   */
  async function aplicarTratamento(
    dados: FormularioProntuario,
    atendidoEmISO: string,
    prontuarioOrigemId: string,
  ): Promise<string | null> {
    if (dados.declaracaoTratamento === 'SEM_TRATAMENTO') {
      // Declarar "sem tratamento" com fármacos em curso é retirá-los NESTA
      // data. Nada é apagado: eles ganham `encerradoEm` e continuam no
      // histórico.
      if (!tratamentoVigente || tratamentoVigente.medicamentos.length === 0) return null;

      const resposta = await sincronizar.mutateAsync({
        id: tratamentoVigente.id,
        data: { atendidoEm: atendidoEmISO, prontuarioOrigemId, medicamentos: [] },
      });
      return descreverSincronizacao(resposta);
    }

    if (dados.declaracaoTratamento !== 'EM_TRATAMENTO') return null;

    const itens = itensParaSincronizar(dados.medicamentos, pesoKg);

    if (tratamentoVigente) {
      if (tratamentoVigente.titulo !== dados.tituloTratamento.trim()) {
        await renomearTratamento.mutateAsync({
          id: tratamentoVigente.id,
          data: { titulo: dados.tituloTratamento.trim() },
        });
      }

      const resposta = await sincronizar.mutateAsync({
        id: tratamentoVigente.id,
        data: { atendidoEm: atendidoEmISO, prontuarioOrigemId, medicamentos: itens },
      });
      return descreverSincronizacao(resposta);
    }

    // Nenhum tratamento em curso nesta data: este atendimento abre um.
    const dia = diaDoAtendimento(atendidoEmISO);
    await criarTratamento.mutateAsync({
      data: {
        pacienteId,
        titulo: dados.tituloTratamento.trim(),
        iniciadoEm: dia,
        prontuarioOrigemId,
        medicamentos: itens.map((item, ordem) => ({
          catalogoFarmacoId: item.catalogoFarmacoId,
          nomeLivre: item.nomeLivre,
          ordem,
          doseInicial: {
            vigenteDe: dia,
            doseValor: item.doseValor,
            doseUnidade: item.doseUnidade,
            dosePorKg: item.dosePorKg,
            pesoKgReferencia: item.pesoKgReferencia,
            frequencia: item.frequencia,
            frequenciaLivre: item.frequenciaLivre,
            via: item.via,
            apresentacaoId: item.apresentacaoId,
            nomeComercialLivre: item.nomeComercialLivre,
          },
        })),
      },
    });

    return itens.length === 1
      ? 'Tratamento iniciado com 1 fármaco.'
      : `Tratamento iniciado com ${itens.length} fármacos.`;
  }

  const aoEnviar = form.handleSubmit(async (bruto) => {
    if (enviando.current) return;
    enviando.current = true;

    try {
      const dados = prontuarioSchema.parse(bruto);
      const atendidoEmISO = localParaISO(dados.atendidoEm);

      // Título de arquivo que acabou de ser digitado, exame recém-marcado,
      // salvamento automático no ar: tudo chega antes do registrar, que fecha
      // o prontuário para sempre.
      await Promise.allSettled([...pendentes.current, salvandoAgora.current]);

      // O formulário vai inteiro: o último salvamento automático pode não ter saído.
      await prontuariosControllerRegistrar(rascunhoId, { ...camposParaEnvio(dados), atendidoEm: atendidoEmISO });
      gravacaoIdRef.current = null;

      const frase = await aplicarTratamento(dados, atendidoEmISO, rascunhoId);

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getProntuariosControllerListarQueryKey() }),
        // Registrado, passa a contar no histórico (o cabeçalho e a linha do tempo).
        queryClient.invalidateQueries({ queryKey: getHistoricoControllerResumoQueryKey(pacienteId) }),
        queryClient.invalidateQueries({ queryKey: getHistoricoControllerLinhaDoTempoQueryKey(pacienteId) }),
        queryClient.invalidateQueries({ queryKey: getTratamentosControllerListarQueryKey() }),
        queryClient.invalidateQueries({
          queryKey: getTratamentoVigenteControllerTratamentoVigenteQueryKey(pacienteId),
        }),
      ]);

      toast.success('Prontuário registrado.');
      if (frase) toast.success(frase);
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(erro, form.setError, 'Não foi possível salvar o prontuário.');
    } finally {
      enviando.current = false;
    }
  });

  const emTratamento = declaracao === 'EM_TRATAMENTO';
  const semTratamento = declaracao === 'SEM_TRATAMENTO';

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && fecharComCuidado()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Novo prontuário
            <span className="text-xs font-normal text-muted-foreground" aria-live="polite">
              {salvamento === 'salvando' ? (
                <span className="inline-flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Salvando rascunho...
                </span>
              ) : salvamento === 'falhou' ? (
                <span className="text-destructive">Não foi possível salvar o rascunho</span>
              ) : (
                <span className="inline-flex items-center gap-1">
                  <Check className="w-3 h-3" /> Rascunho salvo
                </span>
              )}
            </span>
          </DialogTitle>
          <DialogDescription>
            O rascunho é salvo sozinho — pode fechar e continuar depois. Depois de registrado, o prontuário não pode
            ser alterado nem excluído: atualizações entram como anexo.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={aoEnviar} className="space-y-4" noValidate>
          {errors.root?.message && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Campo id="tipo" rotulo="Tipo" obrigatorio>
              <Select value={tipo} onValueChange={(v) => form.setValue('tipo', v)}>
                <SelectTrigger id="tipo" className={CLASSE_CONTROLE} aria-label="Tipo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS.map((t) => (
                    <SelectItem key={t} value={t}>
                      {ROTULO_TIPO[t] ?? t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Campo>

            <Campo
              id="atendidoEm"
              rotulo="Atendimento"
              erro={errors.atendidoEm?.message}
              obrigatorio
            >
              <Input
                id="atendidoEm"
                type="datetime-local"
                className={CLASSE_CONTROLE}
                {...form.register('atendidoEm')}
              />
            </Campo>
          </div>

          <Campo id="titulo" rotulo="Título" dica="Opcional — um resumo de uma linha.">
            <Input id="titulo" className={CLASSE_CONTROLE} {...form.register('titulo')} />
          </Campo>

          {
            (painelDeGravacao ? (
              gravacaoDoRascunhoId && !gravacaoExistente.data ? (
                <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" /> Carregando a gravação da consulta...
                </p>
              ) : (
                <GravacaoDeConsulta
                  pacienteId={pacienteId}
                  prontuarioId={rascunhoId}
                  existente={gravacaoExistente.data ?? null}
                  aoConcluir={aoConcluirGravacao}
                  aoDescartar={() => setPainelDeGravacao(false)}
                  aoMudarEmAndamento={aoMudarGravacaoEmAndamento}
                  aoMudarGravacaoId={aoMudarGravacaoId}
                />
              )
            ) : (
              <Button type="button" variant="outline" onClick={() => setPainelDeGravacao(true)}>
                <Mic className="w-4 h-4" /> Gravar consulta
              </Button>
            ))}

          {textoGeradoPendente && (
            <Alert>
              <AlertDescription className="space-y-2">
                <p>O Conteúdo já tem texto. O que fazer com o rascunho gerado da consulta?</p>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" onClick={() => aplicarTextoGerado('substituir')}>
                    Substituir
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => aplicarTextoGerado('acrescentar')}>
                    Acrescentar no final
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setTextoGeradoPendente(null)}>
                    Manter o que escrevi
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          <Campo id="conteudo" rotulo="Conteúdo" erro={errors.conteudo?.message} obrigatorio>
            <Textarea
              id="conteudo"
              rows={8}
              className={CLASSE_CONTROLE}
              {...form.register('conteudo')}
            />
          </Campo>

          <ArquivosEExames
            pacienteId={pacienteId}
            valor={itens}
            aoMudar={setItens}
            servidor={servidor}
            aoMudarEnviando={setArquivosEnviando}
          />

          <div className="space-y-2">
            <p className="text-sm font-medium text-brand">Tratamento</p>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant={emTratamento ? 'default' : 'outline'}
                aria-pressed={emTratamento}
                onClick={() => declarar('EM_TRATAMENTO')}
              >
                Em tratamento
              </Button>
              <Button
                type="button"
                variant={semTratamento ? 'default' : 'outline'}
                aria-pressed={semTratamento}
                onClick={() => declarar('SEM_TRATAMENTO')}
              >
                Sem tratamento
              </Button>
            </div>

            {!emTratamento && !semTratamento && (
              <p className="text-xs text-muted-foreground">
                Não declarado. É o estado dos prontuários antigos e dos que vêm do aplicativo —
                não é o mesmo que “sem tratamento”.
              </p>
            )}
          </div>

          {/* "Sem tratamento" → NENHUM campo aparece. É o que a spec pede. */}
          {semTratamento && tratamentoVigente && tratamentoVigente.medicamentos.length > 0 && (
            <Alert>
              <AlertDescription>
                Salvar assim retira{' '}
                {tratamentoVigente.medicamentos.length === 1
                  ? 'o fármaco em curso'
                  : `os ${tratamentoVigente.medicamentos.length} fármacos em curso`}{' '}
                nesta data. O histórico de dose continua inteiro.
              </AlertDescription>
            </Alert>
          )}

          {emTratamento && (
            <EditorDeTratamento
              form={form}
              pesoKg={pesoKg}
              carregandoVigente={vigente.isFetching}
            />
          )}

          <div className="space-y-2">
            <p className="text-sm font-medium text-brand">Evolução desde a última avaliação</p>
            <div className="flex flex-wrap gap-2">
              {EVOLUCOES.map((e) => (
                <Button
                  key={e}
                  type="button"
                  variant={evolucao === e ? 'default' : 'outline'}
                  aria-pressed={evolucao === e}
                  onClick={() => marcarEvolucao(e)}
                >
                  {ROTULO_EVOLUCAO[e] ?? e}
                </Button>
              ))}
            </div>
          </div>

          <Campo
            id="evolucaoObservacoes"
            rotulo="Observações da evolução"
            erro={errors.evolucaoObservacoes?.message}
            dica="Opcional — o que sustenta a avaliação acima."
          >
            <Textarea
              id="evolucaoObservacoes"
              rows={3}
              className={CLASSE_CONTROLE}
              {...form.register('evolucaoObservacoes')}
            />
          </Campo>

          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={() => setConfirmandoDescarte(true)}
            >
              <Trash2 className="w-4 h-4" /> Descartar rascunho
            </Button>
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <Button type="button" variant="outline" onClick={fecharComCuidado}>
                Continuar depois
              </Button>
              {/* `isSubmitting` desabilita o botão — o duplo clique não chega a
                  virar duas vigências no mesmo dia. Gravando, também: o
                  prontuário só se salva com a gravação finalizada. */}
              <Button
                type="submit"
                disabled={isSubmitting || gravacaoEmAndamento || arquivosEnviando > 0 || !itensValidos(itens)}
                title={
                  gravacaoEmAndamento
                    ? 'Finalize a gravação da consulta antes de registrar'
                    : arquivosEnviando > 0
                      ? 'Aguarde o envio dos arquivos'
                      : !itensValidos(itens)
                        ? 'Dê um título a cada arquivo'
                        : undefined
                }
              >
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Registrar
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>

      <AlertDialog open={confirmandoFechar} onOpenChange={setConfirmandoFechar}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar a gravação da consulta?</AlertDialogTitle>
            <AlertDialogDescription>
              A consulta ainda está sendo gravada. Fechar agora descarta a gravação e o áudio já enviado — o resto
              do rascunho fica guardado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar gravando</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmandoFechar(false);
                descartarGravacaoEmAndamento();
                void fecharGuardando();
              }}
            >
              Descartar gravação e fechar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmandoDescarte} onOpenChange={setConfirmandoDescarte}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar este rascunho?</AlertDialogTitle>
            <AlertDialogDescription>
              O rascunho é apagado de vez — com o texto, as fotos/PDFs e a gravação da consulta. Os exames
              vinculados continuam no cadastro do paciente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <Button variant="destructive" disabled={descartando} onClick={() => void descartarRascunho()}>
              {descartando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Descartar rascunho
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
